/**
 * CommentPopover - Add comment UI that appears on text selection
 *
 * Features:
 * - Appears near selected text
 * - Quick "Add Comment" button
 * - Expandable input area
 * - Submit/cancel actions
 */

import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles } from 'lucide-react';

interface CommentPopoverProps {
  selectedText: string;
  selectionStart: number;
  selectionEnd: number;
  position: { top: number; left: number };
  onSubmit: (content: string) => void;
  onCancel: () => void;
  onAskMartha?: (selectedText: string) => void;
}

export function CommentPopover({
  selectedText,
  selectionStart,
  selectionEnd,
  position,
  onSubmit,
  onCancel,
  onAskMartha,
}: CommentPopoverProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [content, setContent] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Don't auto-focus the textarea — let the user click into it
  // to preserve the editor text selection highlight

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        if (!isExpanded || !content.trim()) {
          onCancel();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isExpanded, content, onCancel]);

  const handleSubmit = () => {
    if (!content.trim()) return;
    onSubmit(content);
    setContent('');
    setIsExpanded(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      if (content.trim()) {
        // Just collapse, don't lose content
        setIsExpanded(false);
      } else {
        onCancel();
      }
    }
  };

  // Compact view - just buttons
  if (!isExpanded) {
    return (
      <div
        ref={popoverRef}
        data-testid="comment-popover"
        className="fixed z-50 flex items-center gap-1 p-1 bg-white rounded-lg shadow-lg border border-neutral-200"
        onMouseDown={(e) => e.preventDefault()}
        style={{
          top: position.top,
          left: position.left,
          transform: 'translateX(-50%)',
        }}
      >
        <button
          onClick={() => setIsExpanded(true)}
          data-testid="comment-button"
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium
                   text-neutral-700 hover:bg-neutral-100 rounded-md transition-colors"
        >
          <MessageCircle size={14} />
          Comment
        </button>

        {onAskMartha && (
          <>
            <div className="w-px h-4 bg-neutral-200" />
            <button
              onClick={() => onAskMartha(selectedText)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium
                       text-coral-600 hover:bg-coral-50 rounded-md transition-colors"
            >
              <Sparkles size={14} />
              @martha
            </button>
          </>
        )}
      </div>
    );
  }

  // Expanded view - comment input
  return (
    <div
      ref={popoverRef}
      data-testid="comment-popover"
      className="fixed z-50 w-80 bg-white rounded-xl shadow-xl border border-neutral-200 overflow-hidden"
      onMouseDown={(e) => e.preventDefault()}
      style={{
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-neutral-100 bg-neutral-50">
        <span className="text-sm font-medium text-neutral-700">Add Comment</span>
        <button
          onClick={onCancel}
          className="p-1 text-neutral-400 hover:text-neutral-600 rounded transition-colors"
        >
          <X size={16} />
        </button>
      </div>

      {/* Selected text preview */}
      <div className="px-3 py-2 border-b border-neutral-100">
        <p className="text-xs text-neutral-500 mb-1">Selected text:</p>
        <p className="text-sm text-neutral-700 italic line-clamp-2">
          "{selectedText}"
        </p>
      </div>

      {/* Comment input */}
      <div className="p-3">
        <textarea
          ref={textareaRef}
          data-testid="comment-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Write your comment..."
          rows={3}
          className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg resize-none
                   focus:outline-none focus:ring-1 focus:ring-coral-200 focus:border-coral-300"
        />

        <div className="flex items-center justify-between mt-2">
          <p className="text-xs text-neutral-400">
            Cmd+Enter to submit
          </p>

          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100
                       rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!content.trim()}
              data-testid="submit-comment"
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                       bg-coral-500 text-white rounded-lg hover:bg-coral-600
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send size={14} />
              Comment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CommentPopover;
